// ─────────────────────────────────────────────────────────────────────
// TICKET STATUS MODEL — the status pipeline for bug/feature tickets, plus
// migration + active-ticket helpers. Extracted verbatim from the original
// artifact (legacy/App.jsx). Shared by BugArchiveView and BugDetailModal (status
// pills/dropdowns) and by BugReportButton/BugReportModal (active-ticket badge
// counts). No secrets here.
//
// Types are intentionally loose (any), matching the monolith's untyped style.
// ─────────────────────────────────────────────────────────────────────

// Status pipeline for tickets. Each status has a label, color, and
// ordering for the pipeline view. Sorted by pipelineOrder.
export const TICKET_STATUS: any = {
  open: {
    id: 'open',
    label: 'Åpen',
    short: 'Åpen',
    bg: '#fef3c7',
    fg: '#92400e',
    border: '#fcd34d',
    pipeline: 0,
  },
  discussion: {
    id: 'discussion',
    label: 'Til diskusjon',
    short: 'Diskusjon',
    bg: '#cffafe',
    fg: '#155e75',
    border: '#67e8f9',
    pipeline: 1,
  },
  in_progress: {
    id: 'in_progress',
    label: 'Pågår',
    short: 'Pågår',
    bg: '#dbeafe',
    fg: '#1e3a8a',
    border: '#93c5fd',
    pipeline: 2,
  },
  review: {
    id: 'review',
    label: 'Til review',
    short: 'Review',
    bg: '#e9d5ff',
    fg: '#581c87',
    border: '#c4b5fd',
    pipeline: 3,
  },
  approved: {
    id: 'approved',
    label: 'Approved',
    short: 'Approved',
    bg: '#d1fae5',
    fg: '#064e3b',
    border: '#6ee7b7',
    pipeline: 4,
  },
  // Solved = the problem is fixed in code, ready for verification/archiving.
  // Distinguished from closed (archived/fully handled) and rejected — an
  // admin can mark a bug as solved, and then optionally close it later once
  // it has been verified in production.
  solved: {
    id: 'solved',
    label: 'Løst',
    short: 'Løst',
    bg: '#bbf7d0',
    fg: '#14532d',
    border: '#86efac',
    pipeline: 5,
  },
  closed: {
    id: 'closed',
    label: 'Lukket',
    short: 'Lukket',
    bg: '#e5e7eb',
    fg: '#374151',
    border: '#9ca3af',
    pipeline: 6,
  },
  rejected: {
    id: 'rejected',
    label: 'Avvist',
    short: 'Avvist',
    bg: '#fee2e2',
    fg: '#991b1b',
    border: '#fca5a5',
    pipeline: -1,
  },
};
export const TICKET_STATUS_ORDER = [
  'open',
  'discussion',
  'in_progress',
  'review',
  'approved',
  'solved',
  'closed',
  'rejected',
];

// Migrate an old status to the new pipeline. Used when reading bugs from Storage.
// Old 'fixed' → 'closed' (was confirmed done)
// Old 'rejected' → 'rejected' (unchanged)
// Old 'open' / missing → 'open'
export function migrateStatus(bug: any): string {
  if (bug.status === 'fixed') return 'closed';
  if (TICKET_STATUS[bug.status]) return bug.status;
  // Older format: only a fixed boolean
  return bug.fixed ? 'closed' : 'open';
}

// Tickets that need active attention — used for badge counts in the UI
// (floating bug button and main menu). Excludes:
//   - discussion, review: paused, waiting on a response from another party
//   - approved: done from the developer's side, waiting to be closed
//   - closed, rejected: finished
// Active: open, in_progress.
export function isActiveTicket(bug: any): boolean {
  const status = migrateStatus(bug);
  return status === 'open' || status === 'in_progress';
}
